import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Swal from 'sweetalert2';

const FamilyRatingPage = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [planData, setPlanData] = useState(null);
    const [memberName, setMemberName] = useState('');
    const [step, setStep] = useState('welcome'); // 'welcome' or 'voting'
    const [votes, setVotes] = useState({}); // { 'date-slot': selectedDishId }
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchPlanData();
    }, [token]);

    const fetchPlanData = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/rating/${token}`);
            if (res.data.success) {
                setPlanData(res.data.plan);
                // Initialize votes with main dishes
                const initialVotes = {};
                res.data.plan.dishes.forEach(d => {
                    initialVotes[`${d.date}-${d.slot}`] = d.id;
                });
                setVotes(initialVotes);
            }
        } catch (err) {
            Swal.fire('Hata', 'Davet linki geçersiz veya süresi dolmuş', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleVote = (date, slot, dishId) => {
        setVotes(prev => ({ ...prev, [`${date}-${slot}`]: dishId }));
    };

    const handleSubmit = async () => {
        if (!memberName.trim()) {
            Swal.fire('Uyarı', 'Lütfen isminizi giriniz', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const ratings = Object.entries(votes).map(([key, dishId]) => ({
                dishId,
                rating: 5 // Default 'Love' for selected
            }));

            await axios.post(`${API_BASE_URL}/api/rating/submit`, {
                token,
                memberName,
                ratings
            });

            Swal.fire({
                icon: 'success',
                title: 'Harika!',
                text: 'Tercihlerin kaydedildi, plan sahibine iletildi.',
                confirmButtonText: 'Tamam'
            }).then(() => {
                setStep('success');
            });
        } catch (err) {
            Swal.fire('Hata', 'Kaydedilirken bir sorun oluştu', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center bg-slate-50">Yükleniyor...</div>;

    if (step === 'welcome') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 to-rose-100 flex items-center justify-center p-6">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-md w-full text-center">
                    <div className="text-6xl mb-6">👋</div>
                    <h1 className="text-3xl font-black text-slate-900 mb-2">Hoş Geldin!</h1>
                    <p className="text-slate-600 mb-8 font-medium">{planData?.userName} senin için harika bir menü hazırladı. Göz atmadan önce ismini alabilir miyiz?</p>
                    
                    <input 
                        type="text"
                        value={memberName}
                        onChange={(e) => setMemberName(e.target.value)}
                        placeholder="Örn: Derya"
                        className="w-full px-6 py-4 rounded-2xl border-2 border-slate-100 focus:border-orange-400 outline-none text-lg font-bold mb-6 transition-all"
                        onKeyPress={(e) => e.key === 'Enter' && setStep('voting')}
                    />

                    <button 
                        onClick={() => memberName.trim() ? setStep('voting') : Swal.fire('İsim?', 'Lütfen ismini yazar mısın?', 'question')}
                        className="w-full py-4 bg-orange-500 text-white rounded-2xl font-bold text-lg shadow-lg shadow-orange-200 hover:bg-orange-600 transition-all"
                    >
                        Menüye Göz At
                    </button>
                </motion.div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="h-screen bg-green-50 flex flex-col items-center justify-center text-center p-6">
                <div className="text-8xl mb-6">✅</div>
                <h1 className="text-4xl font-black text-slate-900 mb-2">Teşekkürler {memberName}!</h1>
                <p className="text-xl text-slate-600">Tercihlerin {planData?.userName}'e iletildi. Şimdiden afiyet olsun!</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-24 font-sans">
            {/* Header */}
            <div className="bg-white p-6 shadow-sm sticky top-0 z-10 flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">AİLE MENÜSÜ</p>
                    <h1 className="text-xl font-black text-slate-900">{planData?.userName}'in Planı</h1>
                </div>
                <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full font-bold text-sm">
                    👤 {memberName}
                </div>
            </div>

            <div className="max-w-xl mx-auto p-4 space-y-8 mt-4">
                {planData?.dishes.map((dish, idx) => (
                    <div key={idx} className="space-y-4">
                        <div className="flex items-center gap-2">
                            <div className="h-px flex-1 bg-slate-200"></div>
                            <span className="text-[10px] font-black text-slate-400 uppercase px-3">
                                {new Date(dish.date).toLocaleDateString('tr-TR', { weekday: 'long' })} / {dish.slot}
                            </span>
                            <div className="h-px flex-1 bg-slate-200"></div>
                        </div>

                        <div className="grid gap-3">
                            {/* Main Dish & Alternatives */}
                            {[dish, ...(dish.alternatives || [])].map((option, optIdx) => {
                                const isSelected = votes[`${dish.date}-${dish.slot}`] === option.id;
                                return (
                                    <motion.button
                                        key={option.id}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleVote(dish.date, dish.slot, option.id)}
                                        className={`relative flex items-center gap-4 p-4 rounded-3xl text-left transition-all border-2 ${
                                            isSelected 
                                            ? 'bg-white border-orange-500 shadow-xl shadow-orange-100 z-0' 
                                            : 'bg-white/50 border-transparent opacity-70 grayscale-[0.5]'
                                        }`}
                                    >
                                        <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 bg-slate-200">
                                            <img src={option.image_url || 'https://via.placeholder.com/150'} alt={option.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${optIdx === 0 ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
                                                    {optIdx === 0 ? 'Şefin Seçimi' : 'Alternatif'}
                                                </span>
                                                {isSelected && <span className="text-orange-500 text-xl font-bold">✓</span>}
                                            </div>
                                            <h3 className="font-bold text-slate-900 leading-tight">{option.name}</h3>
                                            <p className="text-[11px] text-slate-500 mt-1 capitalize">🍖 {option.mainProtein || 'Sebze'} • {option.category}</p>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Sticky Submit */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-lg border-t border-slate-100">
                <div className="max-w-xl mx-auto">
                    <button 
                        onClick={handleSubmit}
                        className="w-full py-4 bg-slate-900 text-white rounded-[2rem] font-bold text-xl shadow-2xl hover:scale-[1.02] transition-all active:scale-95"
                    >
                        {submitting ? 'Gönderiliyor...' : 'Tercihlerimi Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FamilyRatingPage;
